import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type SaveFn<T> = (value: T) => Promise<void> | void;

export default function useAutosave<T>(value: T, saveFn: SaveFn<T>, pauseMs = 1000) {
  // Always keep the latest value & saveFn in refs (avoids stale closures)
  const valueRef = useRef(value);
  const saveFnRef = useRef(saveFn);

  const [hasInteracted, setHasInteracted] = useState(false); // gate until user edits
  const [saved, setSaved] = useState(false);

  const isDirty = useMemo(
    () => hasInteracted && !Object.is(value, valueRef.current),
    [hasInteracted, value]
  );

  // Relies solely on explicit markTouched() calls to know a value change was a real
  // user edit - inferring it from any value change also fires for non-user updates
  // (e.g. merging a server-assigned id into local state), which would autosave data
  // that hasn't actually been edited yet.
  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    saveFnRef.current = saveFn;
  }, [saveFn]);

  // Debounce timer
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Concurrency control
  const isSavingRef = useRef(false);
  const queuedRef = useRef(false);

  const triggerSave = useCallback(() => {
    // If a save is already in progress, mark that we need to save again.
    if (isSavingRef.current) {
      queuedRef.current = true;
      return;
    }

    isSavingRef.current = true;

    const maybePromise = saveFnRef.current(valueRef.current);

    Promise.resolve(maybePromise)
      .catch((err) => {
        // Optional: bubble this out or integrate with your error handling
        console.error('Autosave error:', err);
      })
      .finally(() => {
        setSaved(true);
        isSavingRef.current = false;

        // If something changed while we were saving, run one more save
        if (queuedRef.current) {
          queuedRef.current = false;
          triggerSave(); // uses the latest valueRef.current
        }
      });
  }, []);

  useEffect(() => {
    if (!isDirty) return;

    // Clear any existing debounce timer
    if (timeoutRef.current != null) {
      clearTimeout(timeoutRef.current);
    }

    // Start a new debounce timer for the latest value
    timeoutRef.current = setTimeout(() => {
      triggerSave();
    }, pauseMs);

    // Cleanup: clear timer when value/pauseMs changes or on unmount
    return () => {
      if (timeoutRef.current != null) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [value, pauseMs, triggerSave, isDirty]);

  // Call this in your input's onChange
  const markTouched = useCallback(() => {
    if (!hasInteracted) {
      setHasInteracted(true);
      setSaved(false);
    }
  }, [hasInteracted]);

  return { saved, isSaving: isSavingRef, markTouched, triggerSave };
}
